import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Upload, 
  ArrowRight,
  FileText,
  Edit2
} from 'lucide-react';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const ApplicationStatus = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasApplication, setHasApplication] = useState(false);
  const [application, setApplication] = useState(null);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Edit form data
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    expertise_area: '',
    bio: '',
    iban: '',
    national_id: '',
    certificate_url: '',
    id_document_url: ''
  });

  const [uploadingCert, setUploadingCert] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);

  useEffect(() => {
    fetchApplication();
  }, []);

  const fetchApplication = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API}/instructor/my-application`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.has_application) {
        setHasApplication(true);
        setApplication(response.data.application);
        setFormData({
          full_name: response.data.application.full_name || '',
          email: response.data.application.email || '',
          phone: response.data.application.phone || '',
          expertise_area: response.data.application.expertise_area || '',
          bio: response.data.application.bio || '',
          iban: response.data.application.iban || '',
          national_id: response.data.application.national_id || '',
          certificate_url: response.data.application.certificate_url || '',
          id_document_url: response.data.application.id_document_url || ''
        });
      } else {
        setHasApplication(false);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, docType) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Lütfen önce giriş yapın');
      navigate('/login');
      return null;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Sadece PDF, JPG, JPEG veya PNG dosyaları yüklenebilir');
      return null;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan küçük olmalıdır');
      return null;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(
        `${API}/upload/document?doc_type=${docType}`,
        formDataUpload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response.data.file_url;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Dosya yüklenirken hata oluştu');
      return null;
    }
  };

  const handleCertificateUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingCert(true);
    const fileUrl = await handleFileUpload(file, 'certificates');
    if (fileUrl) {
      setFormData({ ...formData, certificate_url: fileUrl });
      toast.success('Sertifika başarıyla yüklendi');
    }
    setUploadingCert(false);
  };

  const handleIdUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingId(true);
    const fileUrl = await handleFileUpload(file, 'id_documents');
    if (fileUrl) {
      setFormData({ ...formData, id_document_url: fileUrl });
      toast.success('Kimlik belgesi başarıyla yüklendi');
    }
    setUploadingId(false);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/instructor/my-application`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Başvurunuz güncellendi ve yeniden gönderildi!');
      setEditing(false);
      fetchApplication();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.detail || 'Güncelleme sırasında hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusConfig = {
      pending: {
        icon: Clock,
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        bgColor: 'bg-yellow-50',
        title: 'Başvurunuz İnceleniyor',
        description: 'Başvurunuz admin ekibimiz tarafından incelenmektedir. Genellikle 1-3 iş günü içinde sonuçlanır.'
      },
      approved: {
        icon: CheckCircle2,
        color: 'bg-green-100 text-green-800 border-green-300',
        bgColor: 'bg-green-50',
        title: 'Başvurunuz Onaylandı!',
        description: 'Tebrikler! Artık Pilatika eğitmenisiniz. Eğitmen panelinize gidip kurs oluşturmaya başlayabilirsiniz.'
      },
      rejected: {
        icon: XCircle,
        color: 'bg-red-100 text-red-800 border-red-300',
        bgColor: 'bg-red-50',
        title: 'Başvurunuz Reddedildi',
        description: 'Maalesef başvurunuz onaylanmadı. Admin notlarını inceleyip tekrar başvurabilirsiniz.'
      },
      correction_requested: {
        icon: AlertTriangle,
        color: 'bg-orange-100 text-orange-800 border-orange-300',
        bgColor: 'bg-orange-50',
        title: 'Düzeltme Gerekli',
        description: 'Başvurunuzda bazı düzeltmeler gerekiyor. Lütfen admin notlarını inceleyin ve gerekli değişiklikleri yapın.'
      }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!hasApplication) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="text-center">
            <CardHeader>
              <CardTitle className="text-2xl">Henüz Başvuru Yapmadınız</CardTitle>
              <CardDescription>
                Pilatika'da eğitmen olmak için başvuru yapın ve binlerce kullanıcıya ulaşın.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                className="btn-primary"
                onClick={() => navigate('/become-instructor')}
              >
                Eğitmen Başvurusu Yap
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(application.status);
  const StatusIcon = statusInfo.icon;
  const canEdit = application.status === 'correction_requested' || application.status === 'rejected';

  return (
    <div className="min-h-screen pt-20 wellness-gradient">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Eğitmen Başvuru Durumu</h1>
          <p className="text-gray-600">Başvurunuzun durumunu takip edin</p>
        </div>

        {/* Status Card */}
        <Card className={`mb-8 ${statusInfo.bgColor} border-2 ${statusInfo.color.split(' ')[2]}`}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-full ${statusInfo.color}`}>
                <StatusIcon className="h-8 w-8" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">{statusInfo.title}</h2>
                <p className="text-gray-600 mb-4">{statusInfo.description}</p>
                
                {/* Admin Notes */}
                {application.admin_notes && (
                  <div className="bg-white rounded-lg p-4 border mt-4">
                    <Label className="text-sm font-semibold text-gray-700">Admin Notu:</Label>
                    <p className="mt-1 text-gray-800">{application.admin_notes}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="mt-6 flex gap-3">
                  {application.status === 'approved' && (
                    <Button 
                      className="btn-primary"
                      onClick={() => navigate('/instructor/dashboard')}
                    >
                      Eğitmen Paneline Git
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                  
                  {canEdit && !editing && (
                    <Button 
                      variant="outline"
                      onClick={() => setEditing(true)}
                    >
                      <Edit2 className="mr-2 h-4 w-4" />
                      Düzenle ve Yeniden Gönder
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Form or Application Details */}
        {editing && canEdit ? (
          <Card>
            <CardHeader>
              <CardTitle>Başvuruyu Düzenle</CardTitle>
              <CardDescription>
                Gerekli düzeltmeleri yapın ve başvurunuzu yeniden gönderin
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Ad Soyad *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Telefon *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Uzmanlık Alanı *</Label>
                    <Select
                      value={formData.expertise_area}
                      onValueChange={(value) => setFormData({ ...formData, expertise_area: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pilates">Pilates</SelectItem>
                        <SelectItem value="yoga">Yoga</SelectItem>
                        <SelectItem value="fitness">Fitness</SelectItem>
                        <SelectItem value="nutrition">Beslenme</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="bio">Biyografi</Label>
                  <Textarea
                    id="bio"
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="iban">IBAN *</Label>
                    <Input
                      id="iban"
                      value={formData.iban}
                      onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                      placeholder="TR..."
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="national_id">TC Kimlik No *</Label>
                    <Input
                      id="national_id"
                      value={formData.national_id}
                      onChange={(e) => setFormData({ ...formData, national_id: e.target.value })}
                      maxLength={11}
                      required
                    />
                  </div>
                </div>

                {/* Certificate Upload */}
                <div>
                  <Label>Sertifika/Belge</Label>
                  <div className="mt-2">
                    {formData.certificate_url ? (
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-200">
                        <div className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-600 mr-2" />
                          <span className="text-sm text-green-700">Sertifika yüklendi</span>
                        </div>
                        <label className="cursor-pointer text-sm text-purple-600 hover:text-purple-800">
                          Değiştir
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleCertificateUpload}
                            disabled={uploadingCert}
                          />
                        </label>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center w-full h-24 px-4 transition border-2 border-gray-300 border-dashed rounded-md cursor-pointer hover:border-purple-400">
                        {uploadingCert ? (
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <Upload className="h-6 w-6 text-gray-400" />
                            <span className="text-sm text-gray-600">PDF, JPG, PNG (Max 10MB)</span>
                          </div>
                        )}
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleCertificateUpload}
                          disabled={uploadingCert}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* ID Document Upload */}
                <div>
                  <Label>Kimlik Belgesi (Opsiyonel)</Label>
                  <div className="mt-2">
                    {formData.id_document_url ? (
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-200">
                        <div className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-600 mr-2" />
                          <span className="text-sm text-green-700">Kimlik belgesi yüklendi</span>
                        </div>
                        <label className="cursor-pointer text-sm text-purple-600 hover:text-purple-800">
                          Değiştir
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleIdUpload}
                            disabled={uploadingId}
                          />
                        </label>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center w-full h-24 px-4 transition border-2 border-gray-300 border-dashed rounded-md cursor-pointer hover:border-purple-400">
                        {uploadingId ? (
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <Upload className="h-6 w-6 text-gray-400" />
                            <span className="text-sm text-gray-600">PDF, JPG, PNG (Max 10MB)</span>
                          </div>
                        )}
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleIdUpload}
                          disabled={uploadingId}
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setEditing(false)}
                  >
                    İptal
                  </Button>
                  <Button 
                    type="submit" 
                    className="btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Gönderiliyor...' : 'Yeniden Gönder'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ) : (
          /* Application Details View */
          <Card>
            <CardHeader>
              <CardTitle>Başvuru Detayları</CardTitle>
              <CardDescription>
                Başvuru tarihi: {new Date(application.applied_at).toLocaleDateString('tr-TR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm text-gray-500">Ad Soyad</Label>
                  <p className="font-medium">{application.full_name}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Email</Label>
                  <p className="font-medium">{application.email}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Telefon</Label>
                  <p className="font-medium">{application.phone}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Uzmanlık Alanı</Label>
                  <p className="font-medium capitalize">{application.expertise_area}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm text-gray-500">Biyografi</Label>
                  <p className="font-medium">{application.bio || 'Belirtilmemiş'}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">IBAN</Label>
                  <p className="font-medium font-mono">{application.iban}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">TC Kimlik No</Label>
                  <p className="font-medium font-mono">{application.national_id}</p>
                </div>
              </div>

              {/* Documents */}
              <div className="mt-6 pt-6 border-t">
                <Label className="text-sm text-gray-500 mb-3 block">Yüklenen Belgeler</Label>
                <div className="space-y-2">
                  {application.certificate_url && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-purple-600 mr-2" />
                        <span className="text-sm">Sertifika/Belge</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`${API}${application.certificate_url}`, '_blank')}
                      >
                        Görüntüle
                      </Button>
                    </div>
                  )}
                  {application.id_document_url && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-purple-600 mr-2" />
                        <span className="text-sm">Kimlik Belgesi</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`${API}${application.id_document_url}`, '_blank')}
                      >
                        Görüntüle
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ApplicationStatus;
